import { isDark } from './styles';

export interface ThresholdChartOptions {
  unit?: string;
  thresholdMin?: number;
  thresholdMax?: number;
}

export function darkLineChartOptions(opts: ThresholdChartOptions = {}): any {
  const dark = isDark();
  const unit = opts.unit || '';
  const hasThresholds = opts.thresholdMin != null && opts.thresholdMax != null;

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: dark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: dark ? '#e2e8f0' : '#0f172a',
        bodyColor: dark ? '#94a3b8' : '#475569',
        borderColor: dark ? 'rgba(71, 85, 105, 0.5)' : 'rgba(203, 213, 225, 0.8)',
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          title: (items: any[]) => {
            if (!items.length) return '';
            return new Date(items[0].parsed.x).toLocaleString('en-GB', {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            });
          },
          label: (item: any) => {
            if (item.datasetIndex === 0) {
              const precision = unit === '%' ? 0 : 1;
              const val = item.parsed.y.toFixed(precision);
              if (hasThresholds) {
                const inRange = item.parsed.y >= opts.thresholdMin! && item.parsed.y <= opts.thresholdMax!;
                return `${val}${unit} ${inRange ? '✓' : '⚠ OUT OF RANGE'}`;
              }
              return `${val}${unit}`;
            }
            return '';
          },
          afterBody: () => {
            if (hasThresholds) return `Safe range: ${opts.thresholdMin}${unit} to ${opts.thresholdMax}${unit}`;
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          tooltipFormat: 'PPpp',
          displayFormats: { minute: 'HH:mm', hour: 'HH:mm', day: 'MMM d' },
        },
        grid: { color: dark ? 'rgba(71, 85, 105, 0.2)' : 'rgba(148, 163, 184, 0.15)' },
        ticks: { color: '#64748b', font: { size: 11 }, maxTicksLimit: 10 },
        border: { color: dark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(203, 213, 225, 0.5)' },
      },
      y: {
        grid: { color: dark ? 'rgba(71, 85, 105, 0.2)' : 'rgba(148, 163, 184, 0.15)' },
        ticks: {
          color: '#64748b',
          font: { size: 11 },
          callback: (value: number) => value + unit,
        },
        border: { color: dark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(203, 213, 225, 0.5)' },
      },
    },
  };
}

export interface DatasetConfig {
  label: string;
  data: number[];
  color: string;
  isThreshold?: boolean;
}

export function buildLineDatasets(labels: Date[], datasets: DatasetConfig[]): any[] {
  return datasets.map(ds => {
    if (ds.isThreshold) {
      return {
        label: ds.label,
        data: labels.map(() => ds.data[0]),
        borderColor: ds.color,
        borderWidth: 1.5,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
      };
    }
    return {
      label: ds.label,
      data: ds.data,
      borderColor: ds.color,
      borderWidth: 2,
      fill: false,
      tension: 0.3,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: ds.color,
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
    };
  });
}
